/** Motore di ordinamento centralizzato INGEGO: comparatori, ciclo dei click e preferenze per utente. */
import { useCallback, useEffect, useState } from "react";
import { practiceApi } from "./practiceApi";

const collator = new Intl.Collator("it", { sensitivity: "base", numeric: true });

/** Ordine logico degli stati (non alfabetico): prima ciò che brucia, poi ciò che è chiuso. */
const STATE_GROUPS = [
  ["scaduta", "scaduto", "scadute", "scaduti"],
  ["urgente", "urgenti"],
  ["in scadenza"],
  ["da gestire", "da definire", "da inviare", "da verificare", "non inviata"],
  ["da completare", "da effettuare", "bozza", "da acquisire"],
  ["in corso", "in lavorazione", "in revisione", "aperta", "aperto", "attiva", "attivo"],
  ["programmata", "pronto", "definitivo"],
  ["in attesa esterna", "in attesa", "inviato", "inviata", "sollecitato", "visualizzata"],
  ["regolare", "autorizzato", "effettuata", "presa visione", "accettato", "firmato"],
  ["conclusa", "concluso", "completata", "consegnato", "chiusa", "rifiutato", "archiviata"],
];
const STATE_RANK = {};
STATE_GROUPS.forEach((group, index) => group.forEach(label => { STATE_RANK[label] = index; }));

const plain = value => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/_/g, " ").trim().toLowerCase();

const toNumber = value => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (/^-?\d+(\.\d+)?$/.test(text)) return parseFloat(text);
  const cleaned = text.replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const toIso = value => {
  const text = String(value ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 19);
  const parts = text.match(/^(\d{2})[/.-](\d{2})[/.-](\d{4})$/);
  return parts ? `${parts[3]}-${parts[2]}-${parts[1]}` : "";
};

const isEmpty = (value, kind) => {
  if (value === null || value === undefined || value === "") return true;
  if (kind === "number") return toNumber(value) === null;
  if (kind === "date" || kind === "deadline") return !toIso(value);
  return false;
};

function compareValues(x, y, kind) {
  if (kind === "number") return (toNumber(x) || 0) - (toNumber(y) || 0);
  if (kind === "date" || kind === "deadline") return toIso(x) < toIso(y) ? -1 : toIso(x) > toIso(y) ? 1 : 0;
  if (kind === "bool") return (x ? 1 : 0) - (y ? 1 : 0);
  if (kind === "state") {
    const [a, b] = [plain(x), plain(y)];
    const rank = (STATE_RANK[a] ?? 50) - (STATE_RANK[b] ?? 50);
    return rank || collator.compare(a, b);
  }
  return collator.compare(String(x ?? ""), String(y ?? ""));
}

/** Direzione del primo click: testi e stati in salita, numeri e date dal più recente/alto. */
export const firstDirection = kind => (kind === "number" || kind === "date" ? "desc" : "asc");

/** Click 1 → direzione naturale, click 2 → inversa, click 3 → ordinamento predefinito del workspace. */
export function cycleSort(current, key, kind = "text") {
  const first = firstDirection(kind);
  if (current?.key !== key) return { key, kind, dir: first };
  if (current.dir === first) return { key, kind, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

/** Ordina una copia delle righe: i valori mancanti restano sempre in fondo. */
export function sortRows(rows, sort, get = (row, key) => row[key]) {
  if (!sort?.key || !sort?.dir) return rows;
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const [x, y] = [get(a, sort.key), get(b, sort.key)];
    const [ex, ey] = [isEmpty(x, sort.kind), isEmpty(y, sort.kind)];
    if (ex && ey) return 0;
    if (ex) return 1;
    if (ey) return -1;
    return factor * compareValues(x, y, sort.kind);
  });
}

/** Ordinamento ricordato per utente e per workspace; `null` riporta al predefinito. */
export function useSortPreference(workspace, fallback = null) {
  const [sort, setSort] = useState(fallback);
  useEffect(() => {
    let alive = true;
    practiceApi("/preferences")
      .then(prefs => { if (alive && prefs?.sort_prefs?.[workspace]?.key) setSort(prefs.sort_prefs[workspace]); })
      .catch(() => {});
    return () => { alive = false; };
  }, [workspace]);
  const apply = useCallback(next => {
    setSort(next);
    practiceApi("/preferences", { method: "PUT", body: JSON.stringify({ data: { sort_prefs: { [workspace]: next } } }) }).catch(() => {});
  }, [workspace]);
  const onSort = useCallback((key, kind) => apply(cycleSort(sort, key, kind) || fallback), [apply, sort, fallback]);
  return { sort, apply, onSort };
}
